// YogaVideo.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Card, Box, Typography, Chip, Button, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import SpaIcon from '@mui/icons-material/Spa';
import { getYogaVideoForToday } from '../../service/home';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { Pause, PlayArrow, VolumeUp, VolumeOff, Close, ScreenRotation } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { trackCTA } from '../../service/usercta';
import { getYogaVideoByAccountId } from '../../service/yogaVideo';


// Helper function to get tomorrow's date (example)
const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return {
        date: tomorrow.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        duration: '30 min', // example duration
    };
};

const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const YogaVideo = () => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isYogaVideoOpen, setIsYogaVideoOpen] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [scheduledVideo, setScheduledVideo] = useState(null);
    const [muted, setMuted] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [hasVideoEnded, setHasVideoEnded] = useState(false);
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [sessionEndTime, setSessionEndTime] = useState(null);
    const [videoDurationSec, setVideoDurationSec] = useState(null);
    const [buttonLabel, setButtonLabel] = useState('Join at 6:30 AM');
    const [isJoinEnabled, setIsJoinEnabled] = useState(false);


    const SESSION_START_TIMES = [
        { hour: 6, minute: 30 },
        { hour: 7, minute: 30 },
        { hour: 11, minute: 0 },
        { hour: 18, minute: 30 },
    ].sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));

    const getActiveSessionStart = () => {
        const now = new Date();
        let activeSession = null;

        for (const session of SESSION_START_TIMES) {
            const start = new Date();
            start.setHours(session.hour, session.minute, 0, 0);

            if (now >= start) {
                activeSession = start; // keep updating
            }
        }

        return activeSession;
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setMuted(videoRef.current.muted);
        }
    };

    const DEFAULT_VIDEO_DURATION_MIN = 30;

    useEffect(() => {
        if (!sessionStartTime) return;

        const durationMinutes = videoDurationSec ? Math.ceil(videoDurationSec / 60) : DEFAULT_VIDEO_DURATION_MIN;

        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        const sessionMinutes = SESSION_START_TIMES.map(s => s.hour * 60 + s.minute);

        let newLabel = 'Join at 6:30 AM';
        let joinEnabled = false;

        for (let i = 0; i < sessionMinutes.length; i++) {
            const start = sessionMinutes[i];
            const end = start + durationMinutes;
            const nextStart = sessionMinutes[i + 1];

            if (nowMin < start) {
                newLabel = `Join at ${SESSION_START_TIMES[i].hour}:${SESSION_START_TIMES[i].minute.toString().padStart(2, '0')}`;
                break;
            }

            if (nowMin >= start && nowMin <= end) {
                newLabel = 'Join Now';
                joinEnabled = true;
                break;
            }

            if (nextStart && nowMin > end && nowMin < nextStart) {
                newLabel = `Join at ${SESSION_START_TIMES[i + 1].hour}:${SESSION_START_TIMES[i + 1].minute.toString().padStart(2, '0')}`;
                break;
            }
        }

        // After last session
        if (!joinEnabled) {
            const lastEnd = sessionMinutes.at(-1) + durationMinutes;
            if (nowMin > lastEnd) {
                newLabel = `Join at ${SESSION_START_TIMES[0].hour}:${SESSION_START_TIMES[0].minute.toString().padStart(2, '0')}`;
            }
        }

        console.log({ nowMin, sessionMinutes, durationMinutes, newLabel, joinEnabled });

        setButtonLabel(newLabel);
        setIsJoinEnabled(joinEnabled);
    }, [videoDurationSec, sessionStartTime]);


    useEffect(() => {
        const updateSessionStart = () => {
            const start = getActiveSessionStart();
            setSessionStartTime(start);
        };

        updateSessionStart(); // run immediately

        const interval = setInterval(updateSessionStart, 30 * 1000); // every 30s
        return () => clearInterval(interval);
    }, []);



    // Clean CTA tracking function
    // const handleEventAction = (type) => {
    //     console.log("Button clicked:", type);  // Check if this logs

    //     // 🔥 Get user data properly
    //     const storedUser = localStorage.getItem('user');
    //     const user = storedUser ? JSON.parse(storedUser) : null;

    //     console.log("User from localStorage:", user);  // Verify user data

    //     if (!user || !user.id || !user.account_id) {
    //         console.error('❌ No valid user data found');
    //         return;
    //     }

    //     trackCTA(type, user);

    //     if (type === 'yoga') {
    //         if (!sessionStartTime) return;
    //         setIsYogaVideoOpen(true);
    //     }
    // };

    const handleEventAction = (type) => {
        console.log("Button clicked:", type);

        const storedUser = localStorage.getItem('user');
        const user = storedUser ? JSON.parse(storedUser) : null;

        console.log("User from localStorage:", user);
        console.log("User ID:", user?.id);

        // 🔥 Track CTA event - only check for user.id
        if (user && user.id) {
            trackCTA(type, user, window.location.pathname);
            console.log("✅ CTA tracked successfully");
        } else {
            console.error("❌ CTA Error: Missing user data", {
                hasUser: !!user,
                userId: user?.id
            });
        }

        if (type === 'yoga') {
            if (!sessionStartTime) return;
            setIsYogaVideoOpen(true);
        }
    };

    // const getScheduledVideo = async () => {
    //     const today = new Date();
    //     const yyyy = today.getFullYear();
    //     const mm = String(today.getMonth() + 1).padStart(2, "0");
    //     const dd = String(today.getDate()).padStart(2, "0");
    //     const formattedDate = `${yyyy}${mm}${dd}`; // → yyyymmdd
    //     // const formattedDate = 20251205; // → yyyymmdd

    //     try {
    //         const response = await getYogaVideoForToday(formattedDate);
    //         return {
    //             video: response.track_url,
    //             title: `Yoga Session - ${formattedDate}`,
    //             description: `Your scheduled yoga session`,
    //         };
    //     } catch (error) {
    //         console.error("Error fetching today's yoga video:", error);
    //         return null;
    //     }
    // };

    // useEffect(() => {
    //     const fetchVideo = async () => {
    //         const data = await getScheduledVideo();
    //         setScheduledVideo(data);
    //     };
    //     fetchVideo();
    // }, []);


    const getScheduledVideo = async () => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const formattedDate = `${yyyy}${mm}${dd}`; // → yyyymmdd

        try {
            const response = await getYogaVideoForToday(formattedDate);

            const user = JSON.parse(localStorage.getItem("user"));
            const accountId = user?.account_id;
            const yogaVideos = await getYogaVideoByAccountId(1);
            const todayVideo = yogaVideos?.find(video => {
                const videoDate = new Date(video.date).toISOString().split('T')[0].replace(/-/g, '');
                return videoDate === formattedDate;
            });

            return {
                video: response.track_url,
                title: todayVideo?.title || `Yoga Session - ${formattedDate}`,
                description: todayVideo?.description || `Your scheduled yoga session`,
            };
        } catch (error) {
            console.error("Error fetching today's yoga video:", error);
            return null;
        }
    };
    useEffect(() => {
        const fetchVideo = async () => {
            const data = await getScheduledVideo();
            setScheduledVideo(data);
        };
        fetchVideo();
    }, []);

    const getVideoStartTimeOffset = () => {
        const now = new Date();
        const startTime = new Date();
        // startTime.setHours(7, 30, 0, 0); // 7:30 AM
        startTime.setHours(11, 48, 0, 0); // 7:30 AM


        const endTime = new Date();

        endTime.setHours(12, 20, 0, 0); // 8:00 AM

        if (now < startTime) return 0;
        if (now >= endTime) return 0; // For testing: video will play from start
        return (now - startTime) / 1000; // offset in seconds
    };

    const handleFullscreenToggle = async () => {
        if (!containerRef.current) return;

        try {
            if (document.fullscreenElement || document.webkitFullscreenElement) {
                await handleFullscreenExit();  // exit logic already handles state
            } else {
                const element = containerRef.current;

                // Optimistically set state before async operations
                setIsFullscreen(true);

                if (element.requestFullscreen) await element.requestFullscreen();
                else if (element.webkitRequestFullscreen) await element.webkitRequestFullscreen();

                // Lock landscape on mobile (async)
                if (isMobileDevice() && screen.orientation?.lock) {
                    try {
                        await screen.orientation.lock('landscape');
                    } catch (err) {
                        console.warn('Orientation lock failed:', err);
                    }
                }
            }
        } catch (err) {
            console.error('Fullscreen error:', err);
            // Revert state if something fails
            setIsFullscreen(false);
        }
    };

    const handleFullscreenExit = async () => {
        // Optimistically update state first
        setIsFullscreen(false);

        try {
            if (document.fullscreenElement || document.webkitFullscreenElement) {
                if (document.exitFullscreen) await document.exitFullscreen();
                else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
            }

            // Unlock orientation back to default
            if (screen.orientation && screen.orientation.unlock) {
                screen.orientation.unlock();
            }
            document.body.style.overflow = '';
        } catch (err) {
            console.error('Error exiting fullscreen:', err);
            // If exit failed, you might want to revert state
            setIsFullscreen(true);
        }
    };

    const handlePlay = async () => {
        if (!videoRef.current) return;

        if (isIOS()) {
            // 🔥 iOS: use fake fullscreen instead of native Safari player
            setIsFullscreen(true);
            document.body.style.overflow = 'hidden'; // prevent background scroll
        } else if (isMobileDevice()) {
            try {
                // Android / other mobile: request real fullscreen
                await handleFullscreenToggle();

                // Lock orientation to landscape if supported
                if (screen.orientation && screen.orientation.lock) {
                    await screen.orientation.lock('landscape');
                }
            } catch (err) {
                console.warn('Fullscreen or orientation error:', err);
            }
        }
    };

    const isIOS = () => {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    };

    useEffect(() => {
        if (!sessionStartTime || !videoDurationSec) return;

        const now = new Date();
        const endTime = new Date(sessionStartTime.getTime() + videoDurationSec * 1000);

        let joinEnabled = false;
        let newLabel = `Join at ${sessionStartTime.getHours()}:${String(sessionStartTime.getMinutes()).padStart(2, '0')}`;

        if (now >= sessionStartTime && now <= endTime && !hasVideoEnded) {
            joinEnabled = true;
            newLabel = 'Join Now';
        }

        setIsJoinEnabled(joinEnabled);
        setButtonLabel(newLabel);
    }, [videoDurationSec, sessionStartTime, hasVideoEnded]);

    const getNextSessionStart = () => {
        if (!SESSION_START_TIMES.length) return null;
        const now = new Date();
        const nextSession = SESSION_START_TIMES.find(s => {
            const start = new Date();
            start.setHours(s.hour, s.minute, 0, 0);
            return now < start;
        });
        return nextSession || SESSION_START_TIMES[0]; // wrap to first session if past all
    };


    return (
        <>
            {/* <Card
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: '1px solid #C8E6C9',
                    background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        transform: 'translateY(-1px)'
                    },
                    maxWidth: "765px"
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        flexShrink: 0,
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                        <SpaIcon sx={{ color: 'white', fontSize: 18 }} />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#7843f3', mb: 0.5, fontSize: { xs: '0.85rem', sm: '0.85rem', md: '1rem', lg: '1.1rem' }, }}>
                                Daily Yoga
                            </Typography>
                            <Chip
                                label="Event"
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: { xs: '0.7rem', sm: '0.6rem', md: '0.7rem', lg: '0.8rem' },
                                    bgcolor: '#e8f5e9',
                                    color: '#2e7d32',
                                    '& .MuiChip-label': { p: '0 6px' },
                                    borderRadius: 1
                                }}
                            />
                        </Box>

                        <Typography variant="body2" sx={{ color: '#7843f3', fontSize: { xs: '0.7rem', sm: '0.6rem', md: '0.7rem', lg: '0.8rem' }, mb: 1 }}>
                            {getTomorrow().date} • 6:30 AM  | 7:30 AM | 6:30 PM • {getTomorrow().duration} • Live Session
                        </Typography>

                        <Typography variant="caption" sx={{ color: '#4B5563', fontSize: { xs: '0.7rem', sm: '0.6rem', md: '0.7rem', lg: '0.8rem' }, display: 'block', mb: 1, lineHeight: 1.3 }}>
                            Start your day with gentle yoga to improve flexibility, reduce stress, and enhance mindfulness.
                        </Typography>

                        {(() => {
                            const now = new Date();
                            const currentTime = now.getHours() * 60 + now.getMinutes();
                            // const startTime = 7 * 60 + 30;  // 7:30 AM
                            // const endTime = 8 * 60;          // 8:00 AM

                            const startTime = 11 * 60 + 48;  // 7:30 AM
                            const endTime = 12 * 60 + 20;          // 8:00 AM

                            // const isJoinEnabled =
                            //     Boolean(sessionStartTime) &&
                            //     now >= sessionStartTime &&
                            //     !hasVideoEnded;

                            // 🔍 DEBUG LOGS
                            // console.log('--- JOIN BUTTON DEBUG ---');
                            // console.log('Current time      :', now.toLocaleTimeString());
                            // console.log(
                            //     'Session start time:',
                            //     sessionStartTime ? sessionStartTime.toLocaleTimeString() : 'null'
                            // );
                            // console.log('Has video ended   :', hasVideoEnded);
                            // console.log('Join enabled      :', isJoinEnabled);
                            // console.log('------------------------');

                            // const now = new Date();

                            // const sessionMinutes = SESSION_START_TIMES.map(
                            //     s => s.hour * 60 + s.minute
                            // );

                            // const durationMinutes = videoDurationSec ? Math.ceil(videoDurationSec / 60) : null;


                            // let buttonLabel = 'Join at 6:30 AM';
                            // let isJoinEnabled = false;

                            // if (durationMinutes !== null) {
                            //     const nowMin = now.getHours() * 60 + now.getMinutes();
                            //     const sessionMinutes = SESSION_START_TIMES.map(s => s.hour * 60 + s.minute);

                            //     for (let i = 0; i < sessionMinutes.length; i++) {
                            //         const start = sessionMinutes[i];
                            //         const end = start + durationMinutes;
                            //         const nextStart = sessionMinutes[i + 1];

                            //         if (nowMin < start) {
                            //             buttonLabel = `Join at ${SESSION_START_TIMES[i].hour}:${SESSION_START_TIMES[i].minute.toString().padStart(2, '0')}`;
                            //             break;
                            //         }

                            //         if (nowMin >= start && nowMin <= end) {
                            //             buttonLabel = 'Join Now';
                            //             isJoinEnabled = true;
                            //             break;
                            //         }

                            //         if (nextStart && nowMin > end && nowMin < nextStart) {
                            //             buttonLabel = `Join at ${SESSION_START_TIMES[i + 1].hour}:${SESSION_START_TIMES[i + 1].minute.toString().padStart(2, '0')}`;
                            //             break;
                            //         }
                            //     }

                            //     // After last session
                            //     if (!isJoinEnabled) {
                            //         const lastEnd = sessionMinutes.at(-1) + durationMinutes;
                            //         if (nowMin > lastEnd) {
                            //             buttonLabel = `Join at ${SESSION_START_TIMES[0].hour}:${SESSION_START_TIMES[0].minute.toString().padStart(2, '0')}`;
                            //         }
                            //     }
                            //     console.log({
                            //         nowMin,
                            //         sessionMinutes,
                            //         durationMinutes,
                            //         buttonLabel,
                            //         isJoinEnabled
                            //     });
                            // }



                            return (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                        console.log("Button clicked, isJoinEnabled:", isJoinEnabled);
                                        if (isJoinEnabled) handleEventAction('yoga');
                                    }}
                                    disabled={!isJoinEnabled}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        background: isJoinEnabled
                                            ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)'
                                            : 'linear-gradient(135deg, #9E9E9E 0%, #757575 100%)',
                                        color: 'white',
                                        py: 0.5,
                                        px: 1.5,
                                        borderRadius: 1.5,
                                        fontWeight: 500,
                                        boxShadow: '0 2px 6px rgba(76, 175, 80, 0.2)',
                                        '&:hover': isJoinEnabled ? {
                                            background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)'
                                        } : {},
                                        '&.Mui-disabled': {
                                            background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                                            color: '#9e9e9e'
                                        }
                                    }}
                                >
                                    {buttonLabel}
                                </Button>
                            );
                        })()}
                    </Box>
                </Box>
            </Card> */}

            <Dialog
                open={isYogaVideoOpen}
                onClose={() => {
                    setIsYogaVideoOpen(false);
                    if (videoRef.current) {
                        videoRef.current.pause();
                        videoRef.current.currentTime = 0;
                    }
                    setIsVideoPlaying(false);
                }}
                maxWidth={false} // disable default breakpoints
                PaperProps={{
                    sx: {
                        backgroundColor: '#000',
                        borderRadius: 2,
                        width: 'fit-content',   // popup shrinks to video width
                        maxWidth: '900px',       // responsive for smaller screens
                        overflow: 'hidden',
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#000',
                    color: 'white',
                    p: 2,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}>
                            <SpaIcon sx={{ color: 'white', fontSize: 18 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {scheduledVideo?.title || 'Daily Yoga Session'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
                                {scheduledVideo?.description || 'Daily yoga practice'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setIsYogaVideoOpen(false)}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0, backgroundColor: '#000' }}>
                    {/* 13 nov  */}
                    <Box
                        ref={containerRef}
                        sx={{
                            position: isFullscreen ? 'fixed' : 'relative',
                            top: isFullscreen ? 0 : 'auto',
                            left: isFullscreen ? 0 : 'auto',
                            width: isFullscreen ? '100vw' : 'fit-content',
                            height: isFullscreen ? '100vh' : 'auto',
                            backgroundColor: '#000',
                            zIndex: isFullscreen ? 1400 : 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease-in-out',
                            margin: 'auto',
                        }}
                    >
                        {/*   //videoooo */}
                        <Box
                            component="video"
                            ref={videoRef}
                            src={scheduledVideo?.video}
                            autoPlay
                            playsInline
                            x-webkit-airplay="allow"
                            x5-video-player-type="h5"
                            x5-video-orientation="landscape"
                            x5-video-player-fullscreen="true"
                            muted={muted}
                            controls={false}
                            disablePictureInPicture
                            onPlay={() => {
                                handlePlay(); // keep existing
                                // Add CTA tracking
                                const storedUser = localStorage.getItem('user');
                                const user = storedUser ? JSON.parse(storedUser) : null;
                                if (user && user.id && user.account_id) {
                                    trackCTA('video_play', user, '/yoga');
                                }
                            }}

                            onEnded={() => {
                                setHasVideoEnded(true);
                                setIsYogaVideoOpen(false);
                                setIsVideoPlaying(false);

                                if (videoRef.current) {
                                    videoRef.current.pause();
                                    videoRef.current.currentTime = 0;
                                }

                                if (isFullscreen) {
                                    handleFullscreenToggle(); // exit fullscreen safely
                                }

                                // 🔹 Reset join button to first session
                                const nextSession = getNextSessionStart();
                                setButtonLabel(`Join at ${String(nextSession.hour).padStart(2, '0')}:${String(nextSession.minute).padStart(2, '0')}`);
                                setIsJoinEnabled(false);
                            }}
                            // onLoadedMetadata={() => {
                            //     const offset = getVideoStartTimeOffset();
                            //     if (offset !== null && videoRef.current) {
                            //         videoRef.current.currentTime = offset;
                            //     }
                            // }}
                            onLoadedMetadata={() => {
                                if (!videoRef.current || !sessionStartTime) return;

                                const duration = videoRef.current.duration; // seconds
                                setVideoDurationSec(duration);

                                const end = new Date(sessionStartTime.getTime() + duration * 1000);
                                setSessionEndTime(end);

                                const now = new Date();
                                if (now > sessionStartTime && now < end) {
                                    videoRef.current.currentTime =
                                        (now - sessionStartTime) / 1000;
                                }
                            }}
                            sx={{
                                display: 'block',
                                width: '100%',
                                height: 'auto',
                                maxHeight: isFullscreen ? '100vh' : 'auto',
                                objectFit: 'contain',
                            }}
                        />
                        {/* Fullscreen button */}

                        <IconButton
                            onClick={() => handleFullscreenToggle()} // ✅ one unified call
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                right: 16,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                            }}
                        >
                            {isMobileDevice() ? <ScreenRotation /> : <FullscreenIcon />}
                        </IconButton>

                        {/* Mute/Unmute button */}
                        <IconButton
                            onClick={toggleMute}
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                right: 64,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                            }}
                        >
                            {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                        </IconButton>
                        {isFullscreen && (
                            <IconButton
                                onClick={() => setIsYogaVideoOpen(false)}
                                sx={{
                                    position: 'fixed',
                                    top: 16,
                                    right: 16,
                                    color: 'white',
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    zIndex: 1400,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Box>

                </DialogContent>
            </Dialog>
        </>
    );
};

export default YogaVideo;
